import { findByName } from '@/context/SelectComponent/utils';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

type Find<T, K extends keyof T, R extends keyof T> = {
  key: K;
  return: R;
  defaultVal: T[R];
};

export const useFindById = <T, K1 extends keyof T, R1 extends keyof T>(
  items: T[],
  findId: Find<T, K1, R1>
) => {
  return useCallback(
    (value: string | undefined): T[R1] => {
      if (typeof value === 'undefined' || value === '') {
        return findId.defaultVal;
      }
      return (
        items.find((item) => item[findId.key] === value)?.[findId.return] ||
        findId.defaultVal
      );
    },
    [findId.defaultVal, findId.key, findId.return, items]
  );
};

export const useFindByName = <
  T extends object,
  K1 extends keyof T,
  R1 extends keyof T
>(
  items: T[],
  findName: Find<T, K1, R1>
) => {
  return useCallback(
    (value: string | undefined): T[R1] => {
      if (typeof value === 'undefined' || value === '') {
        return findName.defaultVal;
      }
      const item = findByName(items, value);
      return item?.[findName.return] || findName.defaultVal;
    },
    [findName.defaultVal, findName.return, items]
  );
};

export type BaseOption = {
  label: string;
  value: string | number;
  caption?: string;
};

export type BaseSelectProps = {
  defaultOptions?: BaseOption[];
  cacheQueryKey?: QueryKey;
};

export const useSetCacheQueryData = <T,>(
  cacheQueryKey: QueryKey | undefined,
  item: T
) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (cacheQueryKey && cacheQueryKey.length > 0) {
      queryClient.setQueryData(cacheQueryKey, item);
    }
  }, [cacheQueryKey, item, queryClient]);
};
